import { useQuery } from '@apollo/client'
import _ from 'lodash'
import { useMemo } from 'react'

import { BRAND_LOCATION_GROUP_ID } from 'pared/constants/brands'
import { getBrand } from 'pared/utils/brand'
import {
  toFormattedInteger,
  toPercentString,
  toUsdStr,
} from 'pared/utils/number'

import {
  IListLocationGroupLossPreventionKpisType,
  LIST_LOCATION_GROUP_LOSS_PREVENTION_KPIS,
} from '../gql'
import useLossPreventionConfig, {
  IKpiSettingType,
} from './useLossPreventionConfig'

const useKpis = (
  locationGroupIds: number[],
  isBreakdownByDirectors: boolean,
  startDate?: string,
  endDate?: string,
) => {
  const brand = getBrand()
  const config = useLossPreventionConfig()
  const metrics = useMemo(
    () =>
      config.kpiSettings.reduce(
        (result, k) =>
          k.category === 'basic' && k.fromMetric
            ? [...result, k.fromMetric]
            : result,
        [] as string[],
      ),
    [config.kpiSettings],
  )

  const { data } = useQuery<IListLocationGroupLossPreventionKpisType>(
    LIST_LOCATION_GROUP_LOSS_PREVENTION_KPIS,
    {
      variables: {
        iStartDate: startDate,
        iEndDate: endDate,
        iFilter: {
          location_group_ids: isBreakdownByDirectors
            ? [BRAND_LOCATION_GROUP_ID[brand]]
            : [...locationGroupIds],
          metrics,
        },
        useMetrics: metrics.length > 0,
      },
      skip: !startDate || !endDate || !locationGroupIds,
    },
  )

  return useMemo(() => {
    const rawData = data?.listLocationGroupLossPreventionKpis?.nodes?.[0]
    const metricData = data?.listLocationGroupMetricValues?.nodes.filter(
      (n) => n.metricSummaryData,
    )?.[0].metricSummaryData

    if (!rawData) return []

    const kpis = config?.kpiSettings?.map((kpi: IKpiSettingType) => {
      if (kpi.category === 'basic') {
        const value = (() => {
          if (kpi.fromMetric) {
            const value = metricData?.[kpi.fromMetric].value

            if (_.isNil(value)) return '-'

            return toUsdStr(value / 100.0)
          }

          switch (kpi.key) {
            case 'cashOverOrShort':
              return toUsdStr(rawData?.cashOverOrShort / 100.0)
            case 'oloOverOrShort':
              return toUsdStr(rawData?.oloOverOrShort / 100.0)
            case 'compsTotalAmount':
              return toUsdStr(rawData?.compsTotalAmount / 100.0)
            case 'compsTotalCount':
              return toFormattedInteger(rawData?.compsTotalCount)
            case 'inaccurateCashierShiftPercent':
              return toPercentString(rawData?.inaccurateCashierShiftPercent)
            case 'compsPercent':
              return toPercentString(rawData?.compsPercent)
          }
        })()

        return {
          key: kpi.key,
          value,
          text: kpi.title,
        }
      } else if (kpi.category === 'custom') {
        const data = rawData?.lossPreventionItems?.find(
          (item) => item.type === kpi.type,
        )
        const value = (() => {
          switch (kpi.key) {
            case 'amount':
              return toUsdStr((data?.amount || 0) / 100.0 || '')
            case 'count':
              return toFormattedInteger(data?.count || '')
            case 'checkPercent':
              return toPercentString(data?.checkPercent || '')
          }
        })()

        return {
          key: kpi.type,
          value,
          text: kpi.title,
        }
      } else {
        return { key: '', value: '', text: '' }
      }
    })

    return kpis
  }, [config, data])
}

export default useKpis
